import type { PaginationModel } from '@montugroup/design-system';
import { PaginationVariant, Table } from '@montugroup/design-system';
import { useDebouncedCallback } from '@montugroup/frontend-common';
import { Box, Button, Stack, Typography } from '@mui/material';
import type { Column, Row } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import TableSearchInput from '@/components/common/TableSearchInput';
import { useTableManualPagination } from '@/hooks/table/useTableManualPagination';
import useTableSortingOrder from '@/hooks/table/useTableSortingOrder';
import { getPharmacistDetails } from '@/services/pharmacist.service';
import type { PharmacyProductInventory } from '@montugroup/circuit-shared/types/pharmacy';

import useGetInventoryProducts from '@/hooks/useGetInventoryProducts';

import InventoryAdjustment from './InventoryAdjustment';

const SORTABLE_FIELDS = ['name'] as const satisfies readonly (keyof PharmacyProductInventory)[];

function isSortableField(field: string | undefined): field is (typeof SORTABLE_FIELDS)[number] {
  return SORTABLE_FIELDS.some((sortableField) => sortableField === field);
}

function ProductOverview() {
  const [search, setSearch] = useState('');
  const [showAdjustmentModal, setShowAdjustmentModal] = useState<boolean>(false);
  const {
    paginationModel: { page, pageSize },
    setPaginationModel,
    shouldResetPageIndex,
    setShouldResetPageIndex,
  } = useTableManualPagination({ page: 0, pageSize: 25 });
  const { sortingOrder, handleSortingOrderChange } = useTableSortingOrder();
  const [pharmacyId, setPharmacyId] = useState<null | number>(0);

  useEffect(() => {
    const fetchPharmacistDetails = async () => {
      const { data: pharmacistDetails } = await getPharmacistDetails();
      setPharmacyId(pharmacistDetails.pharmacy_id);
    };
    fetchPharmacistDetails();
  }, []);

  const { products, isLoading, refetch } = useGetInventoryProducts(pharmacyId);

  const handleSearch = useDebouncedCallback((value: string) => {
    setSearch(value);
    setShouldResetPageIndex(true);
  }, 250);

  const { filteredProducts, total } = useMemo(() => {
    if (!products) {
      return { filteredProducts: [], total: 0 };
    }

    const lowercasedSearch = search.toLowerCase();
    const searchedProducts =
      search !== ''
        ? products.filter((product: PharmacyProductInventory) => product.name.toLowerCase().includes(lowercasedSearch))
        : products;

    const sortField = sortingOrder.name;
    const sortedProducts = [...searchedProducts].sort((a, b) => {
      if (!isSortableField(sortField)) {
        return 0;
      }
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue < bValue) {
        return sortingOrder.reverse ? 1 : -1;
      }
      if (aValue > bValue) {
        return sortingOrder.reverse ? -1 : 1;
      }
      return 0;
    });

    const paginatedProducts = sortedProducts.slice(page * pageSize, (page + 1) * pageSize);

    return {
      filteredProducts: paginatedProducts,
      total: searchedProducts.length,
    };
  }, [products, search, sortingOrder, page, pageSize]);

  const renderSimpleValueTableCell = ({
    row,
    column,
  }: {
    row: Row<PharmacyProductInventory>;
    column: Column<PharmacyProductInventory>;
  }) => <Box>{row.getValue(column.id)}</Box>;

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Product Name',
        enableSorting: true,
        cell: renderSimpleValueTableCell,
      },
      {
        accessorKey: 'stock',
        header: 'Stock on Hand',
        enableSorting: false,
        cell: renderSimpleValueTableCell,
      },
      {
        accessorKey: 'allocatedStock',
        header: 'Allocated Stock',
        enableSorting: false,
        cell: renderSimpleValueTableCell,
      },
      {
        accessorKey: 'availableStock',
        header: 'Available Stock',
        enableSorting: false,
        cell: renderSimpleValueTableCell,
      },
    ],
    [renderSimpleValueTableCell],
  );

  const handlePaginationModelChange = useCallback(
    (model: PaginationModel) => {
      setPaginationModel(model);
      setShouldResetPageIndex(false);
    },
    [setPaginationModel, setShouldResetPageIndex],
  );

  return (
    <Box>
      <Typography variant="h5" component="h2" paddingTop={5} paddingBottom={6}>
        Product Overview
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={(theme) => theme.spacing(4)}
        justifyContent="space-between"
        paddingBottom={6}
      >
        <TableSearchInput value={search} onChange={handleSearch} />
        <Box>
          <Button variant="contained" size="large" onClick={() => setShowAdjustmentModal(true)}>
            Add New Adjustment
          </Button>
        </Box>
      </Stack>
      <Table
        data={filteredProducts}
        columns={columns}
        showPagination
        manualPagination
        shouldResetPageIndex={shouldResetPageIndex}
        pageSize={pageSize}
        total={total}
        paginationVariant={PaginationVariant.VARIABLE_PAGE_SIZE}
        onPaginationModelChange={handlePaginationModelChange}
        onSortingChange={handleSortingOrderChange}
        isLoading={isLoading}
      />
      {showAdjustmentModal && (
        <InventoryAdjustment
          handleClose={() => setShowAdjustmentModal(false)}
          refetchProducts={refetch}
          products={products}
          pharmacyId={pharmacyId!}
          showAdjustmentModal={showAdjustmentModal}
        />
      )}
    </Box>
  );
}

export default ProductOverview;
